import {defaultStringifySearch} from '@tanstack/react-router'
import type {URLSearchParamsInit} from './use-navigate'

/**
 * Converts a broadly typed TanStack search object to a URLSearchParams instance.
 *
 * This bridges TanStack's parsed search object to legacy callsites, such as `queryDeps`,
 * that consume `URLSearchParams`. TanStack search values may be complex and its
 * string serialization quotes JSON-looking primitives, so neither representation
 * is directly compatible with the values these callsites expect.
 */
export function tanStackSearchToURLSearchParams(search: Record<string, unknown>): URLSearchParams {
  // Normalize TanStack's parsed search object, preserving its serialization and fallback behavior.
  const normalizedSearch = defaultStringifySearch(search)
  const searchParams = createSearchParams(normalizedSearch)

  for (const [key, value] of Object.entries(search)) {
    if (value === undefined) continue
    // Preserve raw primitives that URLSearchParams consumers expect.
    // TanStack quotes JSON-looking strings for its parser.
    if (value === null || typeof value !== 'object') {
      searchParams.set(key, String(value))
    }
  }

  return searchParams
}

export function createSearchParams(init?: URLSearchParamsInit): URLSearchParams {
  if (!init) return new URLSearchParams()
  if (typeof init === 'string' || init instanceof URLSearchParams || Array.isArray(init))
    return new URLSearchParams(init)

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, item)
      }
    } else {
      params.set(key, value)
    }
  }

  return params
}
